/**
 * Ideal Postcodes lookup (spec §6). Fills the feasibility brief's
 * address/council/uprn/lat/lng from a postcode. `council` is the LPA name, taken
 * from the lookup's `district` field (NOT `county`). Requires a configured API
 * key — when absent, `available` is false and the form's fields stay manual.
 */

import { config } from "@/config/env";

/** Whether a postcode lookup can run (an API key is configured). */
export const postcodeLookupAvailable = !!config.idealPostcodesKey;

export interface PostcodeAddress {
  /** Short premise label for the picker dropdown (e.g. "12 High Street"). */
  label: string;
  /** First address line — fills the lead's `site_address_line_1`. */
  line1: string;
  /** Second / third address lines (raw; often empty). */
  line2: string;
  line3: string;
  /** Post town — fills a job's `city`. */
  postTown: string;
  /** Postal county (the lookup's `county`, e.g. "Surrey") — distinct from
   *  `council`, which is the LPA/district. Fills a job's `county`. */
  county: string;
  /** The postcode — fills the lead's `site_postcode`. */
  postcode: string;
  /** Full single-line address for the brief. */
  address: string;
  council: string;
  uprn: string;
  latitude: number;
  longitude: number;
}

interface IdealPostcodeAddress {
  line_1?: string;
  line_2?: string;
  line_3?: string;
  post_town?: string;
  postcode?: string;
  district?: string;
  county?: string;
  uprn?: string | number;
  latitude?: number;
  longitude?: number;
}

function mapAddress(a: IdealPostcodeAddress): PostcodeAddress {
  const premise = [a.line_1, a.line_2, a.line_3].filter(Boolean).join(", ");
  return {
    label: premise || a.post_town || "Address",
    line1: a.line_1 ?? "",
    line2: a.line_2 ?? "",
    line3: a.line_3 ?? "",
    postTown: a.post_town ?? "",
    county: a.county ?? "",
    postcode: a.postcode ?? "",
    address: [a.line_1, a.line_2, a.line_3, a.post_town].filter(Boolean).join(", "),
    council: a.district ?? "",
    uprn: a.uprn != null ? String(a.uprn) : "",
    latitude: typeof a.latitude === "number" ? a.latitude : 0,
    longitude: typeof a.longitude === "number" ? a.longitude : 0,
  };
}

/** Look up a postcode → all matching addresses (so the caller can let the user
 *  pick the right one). Empty array when unavailable (no key) or no match.
 *  Throws on network/credit errors so the caller can surface a message. */
export async function lookupPostcodeAddresses(postcode: string): Promise<PostcodeAddress[]> {
  const key = config.idealPostcodesKey;
  const pc = postcode.trim();
  if (!key || !pc) return [];

  const url = `https://api.ideal-postcodes.co.uk/v1/postcodes/${encodeURIComponent(pc)}?api_key=${encodeURIComponent(key)}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (res.status === 404) return []; // postcode not found
  if (!res.ok) throw new Error(`Postcode lookup failed (${res.status}).`);

  const data = (await res.json()) as { result?: IdealPostcodeAddress[] };
  return (data.result ?? []).map(mapAddress);
}
